/**
 * Current-user state and auth mutations via TanStack Query (docs/APP_PLAN.md
 * §5.2, §12 M2b). This is the *only* place `apps/web` tracks who is signed
 * in — there is no parallel client-side auth store (TECH_STACK §12, §13).
 *
 * Client-side route protection built on `useCurrentUser()` is a UX
 * convenience only; the server independently authorizes every request
 * regardless of what the client believes (TECH_STACK §40).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '@perplexicon/api-client';
import type { AuthSessionInfo, CurrentUser, ProfileCustomization } from '@perplexicon/api-client';
import type {
  AvatarConfig,
  ChangePasswordRequest,
  LoginRequest,
  PasswordResetComplete,
  PasswordResetRequest,
  RegisterRequest,
} from '@perplexicon/validation';

import { apiClient } from './api-client.js';
import { PROFILE_QUERY_KEY_PREFIX } from './users.js';

export const CURRENT_USER_QUERY_KEY = ['auth', 'me'] as const;
export const AUTH_SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const;

/**
 * The current authenticated user, or `null` if signed out. `UNAUTHENTICATED`
 * is treated as the normal "signed out" result rather than a query error —
 * every anonymous visitor produces it on first load.
 */
export function useCurrentUser(): UseQueryResult<CurrentUser | null> {
  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiClient.auth.me();
      } catch (error) {
        if (
          error instanceof ApiError &&
          (error.code === 'UNAUTHENTICATED' || error.code === 'SESSION_EXPIRED')
        ) {
          return null;
        }
        throw error;
      }
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterRequest) => apiClient.auth.register(input),
    onSuccess: (user) => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, user);
    },
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginRequest) => apiClient.auth.login(input),
    onSuccess: (user) => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.auth.logout(),
    onSuccess: () => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, null);
    },
  });
}

export function useLogoutAll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.auth.logoutAll(),
    onSuccess: () => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, null);
      queryClient.setQueryData(AUTH_SESSIONS_QUERY_KEY, []);
    },
  });
}

/**
 * The signed-in user's own live sessions (CLAUDE.md §9) — where the
 * account is currently reachable from, so unfamiliar activity is
 * noticeable. `enabled` should be `false` while signed out so Settings
 * does not request an endpoint that will just 401.
 */
export function useAuthSessions(enabled: boolean): UseQueryResult<AuthSessionInfo[]> {
  return useQuery({
    queryKey: AUTH_SESSIONS_QUERY_KEY,
    queryFn: () => apiClient.auth.sessions(),
    enabled,
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (input: PasswordResetRequest) => apiClient.auth.requestPasswordReset(input),
  });
}

export function useCompletePasswordReset() {
  return useMutation({
    mutationFn: (input: PasswordResetComplete) => apiClient.auth.completePasswordReset(input),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordRequest) => apiClient.auth.changePassword(input),
  });
}

/**
 * Applies a mutation that changed how the signed-in account *looks* — its
 * avatar or its cosmetic slots.
 *
 * The public profile at `/users/:username` is a separate cache entry
 * (`profileQueryKey`) from the signed-in account, and it renders both the
 * avatar and the cosmetics. Updating only the account therefore leaves the
 * profile page showing a stale snapshot of the user who just edited it.
 * Every appearance mutation goes through here so the two cannot drift apart
 * again: the avatar save previously skipped the invalidation the cosmetics
 * save performed, which is exactly that drift.
 */
export function applyAccountAppearanceUpdate(
  queryClient: QueryClient,
  user: CurrentUser | null,
): void {
  queryClient.setQueryData(CURRENT_USER_QUERY_KEY, user);
  void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY_PREFIX });
}

/**
 * Saves the current user's avatar configuration (APP_SPEC §13) and updates
 * the cached account so every avatar on screen (header, profile, cards)
 * reflects it immediately.
 */
export function updateAvatarMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: (config: AvatarConfig) => apiClient.users.updateAvatar(config),
    onSuccess: (user: CurrentUser) => {
      applyAccountAppearanceUpdate(queryClient, user);
    },
  };
}

export function useUpdateAvatar() {
  const queryClient = useQueryClient();
  return useMutation(updateAvatarMutationOptions(queryClient));
}

/**
 * Saves the current user's profile customization — selected title + the
 * three independent cosmetic slot ids (APP_SPEC §13.2). Updates the cached
 * account and invalidates any cached public-profile view so the change shows
 * on the profile page immediately.
 */
export function updateProfileCustomizationMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: (customization: ProfileCustomization) =>
      apiClient.users.updateProfileCustomization(customization),
    onSuccess: (user: CurrentUser) => {
      applyAccountAppearanceUpdate(queryClient, user);
    },
  };
}

export function useUpdateProfileCustomization() {
  const queryClient = useQueryClient();
  return useMutation(updateProfileCustomizationMutationOptions(queryClient));
}
